/**
 * Accounting periods — close a month, reopen one, record a filed return.
 *
 * WHY THIS SCREEN EXISTS
 *
 * Accounts exports a month and posts it to Tally. Until the month is closed
 * here, nothing stops a late challan being entered with a date inside it —
 * and the two systems then disagree without either of them saying so.
 *
 * Closing does not stop the late challan being recorded. It only stops it
 * landing in a month somebody has already reported. It goes into the current
 * month instead, keeping its own document date, which is how a late invoice
 * has always been handled.
 */
import { useMemo, useState } from 'react'
import {
  CalendarCheck, CalendarX, DatabaseBackup, Lock, LockOpen, ReceiptIndianRupee, ShieldCheck,
} from 'lucide-react'
import {
  Alert, Card, Field, Input, Loading, Modal, Textarea, useToast,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime } from '@/lib/format'

interface Period {
  period_start: string
  period_label: string
  fy_label: string
  status: string
  closed_by_name: string | null
  closed_at: string | null
  reopened_at: string | null
  reopen_reason: string | null
  tax_filed_at: string | null
  tax_reference: string | null
  remarks: string | null
  is_current_month: boolean
}

const PILL: Record<string, string> = {
  OPEN: 'pill-green',
  CLOSED: 'pill-grey',
  PERMANENTLY_CLOSED: 'pill-violet',
}
const LABEL: Record<string, string> = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  PERMANENTLY_CLOSED: 'Permanently closed',
}

export function Periods() {
  const { profile } = useAuth()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [closing, setClosing] = useState<Period | null>(null)
  const [reopening, setReopening] = useState<Period | null>(null)
  const [filing, setFiling] = useState<Period | null>(null)
  const [reason, setReason] = useState('')
  const [ref, setRef] = useState('')
  const [backupOpen, setBackupOpen] = useState(false)
  const [bkFile, setBkFile] = useState('')
  const [bkCount, setBkCount] = useState('')
  const [bkRestore, setBkRestore] = useState(false)
  const [bkRemarks, setBkRemarks] = useState('')

  const role = profile!.role
  const mayClose = ['accounts_spoc', 'admin'].includes(role)
  const mayReopen = role === 'admin'
  const maySee = ['accounts_spoc', 'admin', 'procurement_head', 'project_head', 'cmd', 'cxo',
                  'stores_head'].includes(role)

  const { data: rows, loading, refresh } = useQuery<Period>('v_accounting_periods', () =>
    supabase.from('v_accounting_periods').select('*'), [], { cacheable: false })

  const { data: health, refresh: refreshHealth } = useQuery<any>('v_backup_health', () =>
    supabase.from('v_backup_health').select('*'), [], { cacheable: false })
  const bk = health[0] as any | undefined

  const openCount = useMemo(
    () => rows.filter((r) => r.status === 'OPEN' && !r.is_current_month).length, [rows])

  if (!maySee) {
    return <NotPermitted what="see the accounting periods"
                         who="Accounts, Procurement, the Project Head, the CMD and the CXO" />
  }

  async function run(fn: () => PromiseLike<{ error: { message: string } | null }>, ok: string) {
    setBusy(true)
    try {
      const { error } = await fn()
      if (error) throw new Error(error.message)
      toast.success(ok)
      // Every dialog on this page closes here. The backup one was added later
      // and never added to this line, so it recorded correctly and then sat
      // open, looking as though nothing had happened. Its fields are cleared
      // too, so next month's confirmation does not start with last month's
      // file name already filled in and easy to leave there by accident.
      setClosing(null); setReopening(null); setFiling(null); setReason(''); setRef('')
      setBackupOpen(false); setBkFile(''); setBkCount(''); setBkRestore(false); setBkRemarks('')
      refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not do that')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold text-slate-800">Accounting periods</h1>
      </div>

      <Alert tone="info" title="What closing a month does">
        Closing stops a late document landing in a month Accounts has already exported and posted
        to Tally. It does <strong>not</strong> stop the document being recorded — it keeps its own
        challan or invoice date and is posted in the current month instead, which is how a late
        invoice has always been handled.
        <p className="mt-2">
          Close the previous month on the <strong>10th</strong>, after posting it to Tally.
          Accounts keeps a <strong>10-day grace window</strong> afterwards; nobody else has one.
        </p>
      </Alert>

      {openCount > 1 && mayClose && (
        <Alert tone="warning" title={`${openCount} finished months are still open`}>
          Every month before the current one should be closed once it has been posted to Tally.
          While a month stays open, a back-dated document can still change a figure you have
          already reported.
        </Alert>
      )}

      {['admin', 'cmd', 'cxo', 'project_head'].includes(role) && (
        <Card title="Backups"
              subtitle="The script takes them nightly. This records that somebody has checked they are there">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <DatabaseBackup className={`h-5 w-5 ${
                !bk || bk.health === 'NEVER' ? 'text-red-600'
                  : bk.health === 'STALE' ? 'text-amber-600' : 'text-emerald-600'}`} />
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {!bk || bk.health === 'NEVER'
                    ? 'Never confirmed'
                    : bk.days_ago === 0 ? 'Confirmed today'
                      : `Confirmed ${bk.days_ago} day${bk.days_ago === 1 ? '' : 's'} ago`}
                </p>
                <p className="text-xs text-slate-500">
                  {bk?.confirmed_by_name ? `by ${bk.confirmed_by_name}` : 'Nobody has confirmed a backup yet'}
                  {bk?.files_on_disk ? ` · ${bk.files_on_disk} file(s) on disk` : ''}
                  {bk?.last_restore_test
                    ? ` · restore last tested ${dateTime(bk.last_restore_test)}`
                    : ' · a restore has never been tested'}
                </p>
              </div>
            </div>
            {role === 'admin' && (
              <button className="btn-secondary btn-sm ml-auto" onClick={() => setBackupOpen(true)}>
                Confirm backups
              </button>
            )}
          </div>

          {(!bk || bk.health !== 'OK') && (
            <div className="mt-3">
              <Alert tone={!bk || bk.health === 'NEVER' ? 'error' : 'warning'}
                     title={!bk || bk.health === 'NEVER'
                       ? 'No backup has ever been confirmed'
                       : 'It has been more than a month since anyone checked'}>
                Open <code className="rounded bg-slate-100 px-1 font-mono">D:\OVALMON\INVPROC\backups</code>,
                confirm last night's file is there and is a sensible size, then press
                <strong> Confirm backups</strong>.
                <p className="mt-2">
                  This is a logbook, not a monitor. Nothing here takes a backup — the scheduled
                  script does that. A green light nobody earned is worse than no light.
                </p>
              </Alert>
            </div>
          )}
        </Card>
      )}

      <Card title="The last eighteen months"
            subtitle="A month with no close date has never been closed">
        {loading && <Loading />}
        {!loading && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Month</th><th>Year</th><th>Status</th>
                  <th>Closed by</th><th>Closed on</th>
                  <th>GST return</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.period_start} className={p.is_current_month ? 'bg-oval-50/50' : ''}>
                    <td className="text-sm font-medium">
                      {p.period_label}
                      {p.is_current_month && <span className="ml-2 pill-amber">current</span>}
                    </td>
                    <td className="text-xs text-slate-500">{p.fy_label}</td>
                    <td>
                      <span className={PILL[p.status] ?? 'pill-grey'}>{LABEL[p.status] ?? p.status}</span>
                      {p.reopened_at && (
                        <span className="ml-1.5 pill-amber" title={p.reopen_reason ?? ''}>reopened</span>
                      )}
                    </td>
                    <td className="text-xs">{p.closed_by_name ?? '—'}</td>
                    <td className="text-xs">{p.closed_at ? dateTime(p.closed_at) : '—'}</td>
                    <td className="text-xs">
                      {p.tax_filed_at
                        ? <span className="pill-green" title={p.tax_reference ?? ''}>filed</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {mayClose && p.status === 'OPEN' && !p.is_current_month && (
                          <button className="btn-secondary btn-sm" onClick={() => setClosing(p)}>
                            <Lock className="h-3 w-3" /> Close
                          </button>
                        )}
                        {mayReopen && p.status === 'CLOSED' && (
                          <button className="btn-warning btn-sm" onClick={() => setReopening(p)}>
                            <LockOpen className="h-3 w-3" /> Reopen
                          </button>
                        )}
                        {mayClose && p.status !== 'OPEN' && !p.tax_filed_at && (
                          <button className="btn-ghost btn-sm" onClick={() => setFiling(p)}>
                            <ReceiptIndianRupee className="h-3 w-3" /> Return filed
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------------- close ---------------- */}
      {closing && (
        <Modal open onClose={() => setClosing(null)}
               title={`Close ${closing.period_label}?`}
               subtitle="Do this after the month has been exported and posted to Tally"
               footer={<>
                 <button className="btn-secondary" onClick={() => setClosing(null)} disabled={busy}>Cancel</button>
                 <button className="btn-primary" disabled={busy}
                         onClick={() => run(() => supabase.rpc('rpc_close_period', {
                           p_period_start: closing.period_start, p_remarks: reason || null,
                         }), `${closing.period_label} is closed.`)}>
                   {busy ? 'Closing…' : 'Close the month'}
                 </button>
               </>}>
          <Alert tone="info" title="What happens next">
            Anything dated in {closing.period_label} entered from now on keeps that document date
            and is posted in the current month instead. <strong>Nothing is refused</strong> and
            nothing is lost.
            <p className="mt-2">
              You keep a <strong>10-day grace window</strong> to post into it yourself. Everyone
              else is out immediately.
            </p>
          </Alert>
          <div className="mt-3">
            <Field label="Note (optional)" hint="e.g. the Tally voucher batch, or the export file name">
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="Posted to Tally, batch AUG-2026" />
            </Field>
          </div>
        </Modal>
      )}

      {/* ---------------- reopen ---------------- */}
      {reopening && (
        <Modal open onClose={() => setReopening(null)}
               title={`Reopen ${reopening.period_label}?`}
               subtitle="The CMD will be notified"
               footer={<>
                 <button className="btn-secondary" onClick={() => setReopening(null)} disabled={busy}>Cancel</button>
                 <button className="btn-warning" disabled={busy}
                         onClick={() => run(() => supabase.rpc('rpc_reopen_period', {
                           p_period_start: reopening.period_start, p_reason: reason.trim(),
                         }), `${reopening.period_label} is open again.`)}>
                   {busy ? 'Reopening…' : 'Reopen it'}
                 </button>
               </>}>
          <Alert tone="warning" title="Accounts has already reported this month">
            Reopening lets documents land back in {reopening.period_label}, which means this system
            and Tally will differ until Accounts re-exports it and posts the difference.
            <p className="mt-2">
              <strong>The CMD and Accounts are both notified</strong>, with your reason. That is
              deliberate — reopening a reported month should never be quiet.
            </p>
          </Alert>
          <div className="mt-3">
            <Field label="Why is it being reopened?" required
                   hint="A full sentence. Kept permanently, and shown to the CMD.">
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Challan CH-4471 from 26-08 was found at Khubal and must sit in August to match the GRN raised against PO-045." />
            </Field>
          </div>
        </Modal>
      )}

      {/* ---------------- tax filed ---------------- */}
      {filing && (
        <Modal open onClose={() => setFiling(null)}
               title={`Record the GST return for ${filing.period_label}`}
               subtitle="Locks the tax figures for that month"
               footer={<>
                 <button className="btn-secondary" onClick={() => setFiling(null)} disabled={busy}>Cancel</button>
                 <button className="btn-primary" disabled={busy}
                         onClick={() => run(() => supabase.rpc('rpc_mark_tax_filed', {
                           p_period_start: filing.period_start, p_reference: ref.trim(),
                         }), 'Return recorded.')}>
                   {busy ? 'Saving…' : 'Record it'}
                 </button>
               </>}>
          <Alert tone="info" title="Why this is separate from closing the month">
            Filing a return is more final than closing a month. Once GSTR-3B has gone in, the tax
            figures for {filing.period_label} must not move — even if the month is briefly reopened
            to correct a stock error.
          </Alert>
          <div className="mt-3">
            <Field label="Acknowledgement / ARN" required>
              <Input value={ref} onChange={(e) => setRef(e.target.value)}
                     placeholder="AA160826123456X" />
            </Field>
          </div>
        </Modal>
      )}

      {backupOpen && (
        <Modal open onClose={() => setBackupOpen(false)}
               title="Confirm the backups are on disk"
               subtitle="Look in the folder first. This records what you saw."
               footer={<>
                 <button className="btn-secondary" onClick={() => setBackupOpen(false)} disabled={busy}>Cancel</button>
                 <button className="btn-primary" disabled={busy}
                         onClick={() => run(() => supabase.rpc('rpc_confirm_backup', {
                           p_latest_file: bkFile || null,
                           p_files_on_disk: bkCount ? Number(bkCount) : null,
                           p_restore_tested: bkRestore,
                           p_remarks: bkRemarks || null,
                         }), 'Backup confirmation recorded.').then(() => refreshHealth())}>
                   {busy ? 'Recording…' : 'Record it'}
                 </button>
               </>}>
          <Alert tone="info" title="What to check before you press this">
            <ul className="mt-1 space-y-1 text-sm">
              <li>Last night's file exists in <code>D:\OVALMON\INVPROC\backups</code></li>
              <li>It is a sensible size for the data — around a megabyte or more,
                  not a few kilobytes</li>
              <li><code>backup.log</code> ends with <strong>schema and data present</strong> —
                  that phrase only appears after the script has opened the file and found
                  real rows. It is worth more than the size</li>
            </ul>
          </Alert>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field label="Newest file name" hint="e.g. oval-ims_2026-08-26_0230.sql">
              <Input value={bkFile} onChange={(e) => setBkFile(e.target.value)} />
            </Field>
            <Field label="How many files are in the folder?">
              <Input type="number" value={bkCount} onChange={(e) => setBkCount(e.target.value)} />
            </Field>
            <label className="flex items-start gap-2 text-sm sm:col-span-2">
              <input type="checkbox" className="mt-1" checked={bkRestore}
                     onChange={(e) => setBkRestore(e.target.checked)} />
              <span>
                <strong>I actually restored one into a scratch project and it worked.</strong>
                <span className="block text-xs text-slate-500">
                  Do this at least once a quarter. A backup nobody has ever restored is a
                  hypothesis, not a backup.
                </span>
              </span>
            </label>
            <Field label="Anything worth noting" className="sm:col-span-2">
              <Textarea rows={2} value={bkRemarks} onChange={(e) => setBkRemarks(e.target.value)} />
            </Field>
          </div>
        </Modal>
      )}

      <Alert tone="info" title="The three states">
        <ul className="mt-1 space-y-1 text-sm">
          <li><ShieldCheck className="mr-1 inline h-3.5 w-3.5" /><strong>Open</strong> — anybody
            may post into it.</li>
          <li><CalendarCheck className="mr-1 inline h-3.5 w-3.5" /><strong>Closed</strong> —
            Accounts only, for ten days. An administrator may reopen it, and the CMD is told.</li>
          <li><CalendarX className="mr-1 inline h-3.5 w-3.5" /><strong>Permanently closed</strong> —
            nobody, ever. Set this after the statutory audit for the year is signed.</li>
        </ul>
      </Alert>
    </div>
  )
}
